import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Send, Eye, MessageSquarePlus, Pin, Lock, Image as ImageIcon, Sparkles,
} from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { canGroup } from '../lib/rbac';
import RichContentBlocks from '../components/RichContentBlocks';
import RichContentBlocksEditor from '../components/RichContentBlocksEditor';
import RichTextEditor from '../components/RichTextEditor';
import InternalAccessGuard from '../components/InternalAccessGuard';
import useForumAccess from '../components/forum/useForumAccess';

function avatarInitials(name) {
  return (name || '?').split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();
}

function NewThreadInner() {
  const { lang, slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMod = canGroup(user, 'forum');
  const access = useForumAccess();

  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', body: '', body_html: '', body_blocks: [], is_pinned: false, is_locked: false });
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');

  const base = `/${lang}/${lang === 'en' ? 'forum' : 'foro'}`;

  useEffect(() => {
    let alive = true;
    api.get('/forum/categories')
      .then(({ data }) => {
        if (!alive) return;
        setCategory((data || []).find((c) => c.slug === slug) || null);
      })
      .catch((e) => alive && setErr(formatApiError(e.response?.data?.detail) || 'Error'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const htmlEmpty = (h) => !h || !h.replace(/<(.|\n)*?>/g, '').trim();
  const canPublish = form.title.trim() && (!htmlEmpty(form.body_html) || form.body.trim() || form.body_blocks.length > 0);

  async function publish() {
    if (!canPublish || !category) return;
    setCreating(true); setErr('');
    try {
      const { data } = await api.post('/forum/threads', {
        category_id: category.id,
        title: form.title,
        body: form.body,
        body_html: htmlEmpty(form.body_html) ? '' : form.body_html,
        body_blocks: form.body_blocks,
        is_pinned: isMod ? form.is_pinned : false,
        is_locked: isMod ? form.is_locked : false,
      });
      navigate(`/${lang}/${lang === 'en' ? 'forum' : 'foro'}/t/${data.id}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Error');
      setCreating(false);
    }
  }

  const inputCls = 'w-full rounded-lg border border-charcoal-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500 transition-all';

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={28} /></div>;
  }
  if (!category) {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-28 pb-16 text-center">
        <p className="text-charcoal-500">Categoría no encontrada.</p>
        <Link to={base} className="text-cmic-600 text-sm font-medium mt-3 inline-block">← Volver al foro</Link>
      </div>
    );
  }
  if (!access.can_post) {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-28 pb-16">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center" data-testid="forum-nopost-notice">
          <p className="text-amber-800 font-medium">No tienes permiso para crear temas nuevos en el foro.</p>
          <p className="text-amber-700 text-sm mt-1">
            {access.level === 'read'
              ? 'Tu acceso es de solo lectura. Puedes leer los debates existentes.'
              : 'Puedes responder en los temas existentes, pero no iniciar nuevos.'}
          </p>
          <Link to={base} className="inline-flex items-center gap-1.5 mt-4 text-cmic-700 font-medium hover:underline">
            <ArrowLeft size={15} /> Volver al foro
          </Link>
        </div>
      </div>
    );
  }


  return (
    <div data-testid="forum-new-thread-page" className="bg-gray-50 min-h-screen pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-cmic-700 to-cmic-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to={`/${lang}/${lang === 'en' ? 'forum' : 'foro'}/c/${slug}`} className="inline-flex items-center gap-1.5 text-xs text-cmic-100 hover:text-white mb-3">
            <ArrowLeft size={14} /> {category.name}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2.5">
            <MessageSquarePlus size={26} /> Iniciar un nuevo debate
          </h1>
          <p className="text-cmic-100 text-sm mt-1.5">Comparte una pregunta, recurso o tema con la comunidad de <strong>{category.name}</strong>.</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-12 gap-8">
        {/* ── Composer ─────────────────────────────────────────────── */}
        <div className="lg:col-span-7 space-y-5">
          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="forum-new-error">{err}</div>}

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
            <div>
              <label className="block text-xs font-semibold tracking-[0.04em] uppercase text-charcoal-500 mb-2">Título del debate</label>
              <input
                type="text" value={form.title} onChange={(e) => set({ title: e.target.value })}
                data-testid="forum-new-thread-title" maxLength={160} autoFocus
                placeholder="Ej: ¿Qué plataforma de RSI recomiendan para eventos híbridos?"
                className={`${inputCls} text-base font-medium`}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold tracking-[0.04em] uppercase text-charcoal-500 mb-2">Mensaje</label>
              <RichTextEditor
                value={form.body_html}
                onChange={(html) => set({ body_html: html })}
                testid="forum-new-thread-body"
                placeholder="Desarrolla tu idea, comparte contexto y plantea tu pregunta…"
                minHeight={200}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold tracking-[0.04em] uppercase text-charcoal-500 mb-2 flex items-center gap-1.5">
                <ImageIcon size={13} /> Adjuntos y archivos <span className="font-normal normal-case text-charcoal-400">(opcional)</span>
              </label>
              <RichContentBlocksEditor value={form.body_blocks} onChange={(blocks) => set({ body_blocks: blocks })} />
            </div>

            {isMod && (
              <div className="border-t border-gray-100 pt-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-600 mb-2 flex items-center gap-1.5"><Sparkles size={12} /> Opciones de moderador</p>
                <div className="flex flex-wrap gap-3">
                  <ToggleChip active={form.is_pinned} onClick={() => set({ is_pinned: !form.is_pinned })} icon={Pin} label="Fijar arriba" testid="forum-new-pin" />
                  <ToggleChip active={form.is_locked} onClick={() => set({ is_locked: !form.is_locked })} icon={Lock} label="Cerrar a respuestas" testid="forum-new-lock" />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <Link to={`/${lang}/${lang === 'en' ? 'forum' : 'foro'}/c/${slug}`} className="text-sm font-medium text-charcoal-500 hover:text-charcoal-700 px-2">Cancelar</Link>
            <button
              onClick={publish} disabled={creating || !canPublish}
              data-testid="forum-new-thread-publish"
              className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-7 py-3 rounded-xl text-sm font-bold shadow-md transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none"
            >
              {creating ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Publicar debate
            </button>
          </div>
        </div>

        {/* ── Live preview ─────────────────────────────────────────── */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="text-xs font-semibold tracking-[0.05em] uppercase text-charcoal-500 mb-3 flex items-center gap-1.5"><Eye size={13} /> Vista previa</div>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="forum-new-preview">
              <div className={`px-5 py-4 border-b border-gray-100 ${form.is_pinned ? 'bg-amber-50' : 'bg-gray-50/60'}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  {form.is_pinned && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded"><Pin size={9} /> Fijado</span>}
                  {form.is_locked && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-charcoal-200 text-charcoal-600 px-2 py-0.5 rounded"><Lock size={9} /> Cerrado</span>}
                </div>
                <h3 className="text-lg font-bold text-charcoal-900 leading-snug break-words">
                  {form.title || <span className="text-charcoal-300">Título del debate…</span>}
                </h3>
              </div>
              <div className="flex gap-3 p-5">
                <div className="w-9 h-9 rounded-full bg-cmic-100 text-cmic-700 flex items-center justify-center font-semibold text-xs flex-shrink-0">
                  {avatarInitials(user?.full_name || user?.username)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-charcoal-800">{user?.full_name || user?.username}</div>
                  <div className="text-[11px] text-charcoal-400 mb-2">ahora mismo</div>
                  {form.body
                    ? <div className="text-sm text-charcoal-700 leading-relaxed whitespace-pre-wrap break-words">{form.body}</div>
                    : <p className="text-sm text-charcoal-300">El contenido aparecerá aquí…</p>}
                  <RichContentBlocks blocks={form.body_blocks} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleChip({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-all ${active ? 'bg-cmic-600 text-white border-cmic-600 shadow-sm' : 'bg-white text-charcoal-600 border-charcoal-200 hover:border-cmic-300'}`}
    >
      <Icon size={14} /> {label}
    </button>
  );
}

export default function ForumNewThreadPage() {
  return (
    <InternalAccessGuard>
      <NewThreadInner />
    </InternalAccessGuard>
  );
}
